import {TSAnlaufstelleType} from './enums/TSAnlaufstelleType';
import {TSAnrede} from './enums/TSAnrede';
import {TSKantonCode} from './enums/TSKantonCode';
import {TSRole} from './enums/TSRole';
import {TSSprache} from './enums/TSSprache';
import TSAbstractEntity from './TSAbstractEntity';
import TSAnlaufstelle from './TSAnlaufstelle';
import TSGemeinde from './TSGemeinde';
import TSPasswordData from './TSPasswordData';
import {TSSerializable} from './TSSerializable';

export default class TSBenutzer extends TSAbstractEntity implements TSSerializable<TSBenutzer> {
    private _nachname: string;
    private _vorname: string;
    private _passwordData: TSPasswordData;
    private _email: string;
    private _kantonen: string;
    private _telefon: string;
    private _emailRepeat: string; //this field is only for validation, it is NOT passed to server
    private _roles: Array<TSRole>;
    private _korrespondenzsprache: TSSprache;
    private _anrede: TSAnrede;
    private _inaktiv: boolean | undefined = undefined;
    private _anlaufstellen: Array<TSAnlaufstelle>;
    private _recaptchaResponse: string | undefined;
    private _maxErfassungsbetrag: number = 0; //this is default on server anyway

    constructor(vorname?: string, nachname?: string, username?: string, password?: string, email?: string,
                roles?: Array<TSRole>, anlaufstellen?: Array<TSAnlaufstelle>,
    ) {

        super();
        this._vorname = vorname || '';
        this._nachname = nachname || '';
        this._email = email || '';
        this._roles = roles || [];
        this._anlaufstellen = anlaufstellen || [];
    }

    public get anlaufstelleList(): string {
        return this._anlaufstellen.map(x => x.name).join(', ');
    }

    //it creates a comma separated list with the abbreviations of the cantons concerned
    //   - in case of a TSAnlaufstelleType.KANTON the canton is taken directly from the Anlaufstelle
    //   - in case of a TSAnlaufstelleType.GEMEINDE the canton is taken from the Gemeinde which belongs to the Anlaufstelle
    private initKantonList(): void {
        let gemeinden: TSGemeinde[] = this.getGemeindenFromAnlaufstellen();
        let kantonArray: Array<TSKantonCode> = new Array<TSKantonCode>();
        kantonArray.push(...gemeinden.map(x => x.kanton));
        kantonArray.push(...(this.getKantoneFromAnlaufstellen()));

        this._kantonen = TSBenutzer.removeDuplicates(kantonArray).join(', ');
    }

    public get kantonList(): string {
        if (!this._kantonen) {
            this.initKantonList();
        }
        return this._kantonen;
    }

    private static removeDuplicates(kantonArray: Array<TSKantonCode>): Array<TSKantonCode> {
        return Array.from(new Set(kantonArray));
    }

    private getKantoneFromAnlaufstellen() {
        let kantonaleAnlaufstellen = this.getAnlaufstellenForType(TSAnlaufstelleType.KANTON);
        let kantone: TSKantonCode[] = new Array<TSKantonCode>();

        kantonaleAnlaufstellen.forEach(anlaufstelle => {
            if (anlaufstelle.kanton) {
                //no undefined will be added
                kantone.push(anlaufstelle.kanton);
            }
        });

        return kantone;
    }

    private getAnlaufstellenForType(type: TSAnlaufstelleType) {
        return this._anlaufstellen.filter(x => x.anlaufstelleType === type);
    }

    private getGemeindenFromAnlaufstellen(): TSGemeinde[] {
        let gemeindeAnlaufstellen = this.getAnlaufstellenForType(TSAnlaufstelleType.GEMEINDE);
        let gemeinden: TSGemeinde[] = [];

        //no flatMap in ES5, so DIY
        gemeindeAnlaufstellen.forEach(anlaufstelle => gemeinden = gemeinden.concat(anlaufstelle.gemeinden));
        return gemeinden;
    }

    public get nachname(): string {
        return this._nachname;
    }

    public set nachname(value: string) {
        this._nachname = value;
    }

    public get vorname(): string {
        return this._vorname;
    }

    public set vorname(value: string) {
        this._vorname = value;
    }

    public get email(): string {
        return this._email;
    }

    public set email(value: string) {
        this._email = value;
    }

    public get emailRepeat(): string {
        return this._emailRepeat;
    }

    public set emailRepeat(value: string) {
        this._emailRepeat = value;
    }

    public get telefon(): string {
        return this._telefon;
    }

    public set telefon(value: string) {
        this._telefon = value;
    }

    public get roles(): Array<TSRole> {
        return this._roles;
    }

    public set roles(value: Array<TSRole>) {
        this._roles = value;
    }

    public get korrespondenzsprache(): TSSprache {
        return this._korrespondenzsprache;
    }

    public set korrespondenzsprache(value: TSSprache) {
        this._korrespondenzsprache = value;
    }

    public get anrede(): TSAnrede {
        return this._anrede;
    }

    public set anrede(value: TSAnrede) {
        this._anrede = value;
    }

    public get inaktiv(): boolean | undefined {
        return this._inaktiv;
    }

    public get passwordData(): TSPasswordData {
        return this._passwordData;
    }

    public set passwordData(value: TSPasswordData) {
        this._passwordData = value;
    }

    public set inaktiv(value: boolean | undefined) {
        this._inaktiv = value;
    }

    public get anlaufstellen(): Array<TSAnlaufstelle> {
        return this._anlaufstellen;
    }

    public set anlaufstellen(value: Array<TSAnlaufstelle>) {
        this._anlaufstellen = value;
    }

    public getFullName(): string {
        return (this.vorname ? this.vorname : '') + ' ' + (this.nachname ? this.nachname : '');
    }

    public get recaptchaResponse(): string | any {
        return this._recaptchaResponse;
    }

    public set recaptchaResponse(value: string | any) {
        this._recaptchaResponse = value;
    }

    public get maxErfassungsbetrag(): number {
        return this._maxErfassungsbetrag;
    }

    public set maxErfassungsbetrag(value: number) {
        this._maxErfassungsbetrag = value;
    }

    public static deserializeArray(data: any): Array<TSBenutzer> {
        let benutzerArray: Array<TSBenutzer> = [];
        if (data && Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                benutzerArray[i] = new TSBenutzer().deserialize(data[i]);
            }
        } else {
            benutzerArray[0] = new TSBenutzer().deserialize(data);
        }
        return benutzerArray;
    }

    public static getDeserializer = (benutzer: any) => {
        return new TSBenutzer().deserialize(benutzer);
    };

    public checkEmailRepeated(): boolean {
        if (this.email) {
            return this.email === this._emailRepeat;
        }
        return true;
    }

    public deserialize(restObject: any): TSBenutzer {
        super.deserialize(restObject);
        this.vorname = restObject.vorname;
        this.nachname = restObject.nachname;
        this.korrespondenzsprache = restObject.korrespondenzsprache;
        this.anrede = restObject.anrede;
        this.email = restObject.email;
        this.telefon = restObject.telefon;
        this.roles = restObject.roles;
        if (restObject.anlaufstellen) {
            this.anlaufstellen = restObject.anlaufstellen.map((anlaufstelle: any) => {
                return (new TSAnlaufstelle()).deserialize(anlaufstelle)
            });
        }
        this.inaktiv = restObject.inaktiv;
        this.maxErfassungsbetrag = restObject.maxErfassungsbetrag;
        return this;
    }

    public serialize(restObject: any): any {
        super.serialize(restObject);
        restObject.vorname = this.vorname;
        restObject.nachname = this.nachname;
        restObject.korrespondenzsprache = this.korrespondenzsprache;
        restObject.anrede = this.anrede;
        if (this.passwordData) {
            restObject.passwordData = this.passwordData.serialize({});
        }
        restObject.email = this.email;
        restObject.telefon = this.telefon;
        restObject.roles = this.roles;
        restObject.anlaufstellen = this.arrayToRestObject(this.anlaufstellen);
        restObject.inaktiv = this.inaktiv;
        restObject.recaptchaResponse = this.recaptchaResponse;
        restObject.maxErfassungsbetrag = this.maxErfassungsbetrag;
        return restObject;
    }
};



// WEBPACK FOOTER //
// ./src/app/models/TSBenutzer.ts